import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';

import type { PageSlug } from './constants';
import { informationalPageKey } from './queries';
import type { WireBodyFields } from './types';

/**
 * The one write this surface owns.
 *
 * `PATCH /api/v1/admin/informationalPages/{slug}` is `exclude_unset` on the
 * service side, so a key the console omits is a key it leaves alone — which is
 * why the About page sends `bodyHtml` and no `bodyFields.social`, and the
 * Contact page sends `bodyFields` and no `bodyHtml`. Sending both would have
 * each page overwrite the half it does not draw.
 *
 * **Nothing auto-retries and nothing is optimistic.** The service sanitises the
 * body on write and refuses what falls outside its allow-list with
 * `422 CONTENT_UNSAFE_PAGE_BODY` naming each violation; a client that retried, or
 * that painted the save before the answer, would be reporting an outcome it does
 * not have. **No confirmation dialog either** — §20.17 grants the body and asks
 * for no confirm step, and §12.3 reserves the dialog for what destroys and for a
 * configuration write. Saving prose is neither.
 *
 * The invalidation set is exactly the tuple the read registered. It reaches no
 * further on purpose: an informational body feeds no vocabulary, no queue depth,
 * and no dashboard figure anywhere else in the console.
 */
export interface InformationalPageWrite {
  bodyHtml?: string;
  bodyFields?: WireBodyFields;
}

export function useUpdateInformationalPage(slug: PageSlug) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: InformationalPageWrite) =>
      api.patch<null>('content', `/admin/informationalPages/${slug}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: informationalPageKey(slug) });
    },
    retry: false,
  });
}
